'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { CurrencyInput } from '@kit/ui/currency-input';
import { DateField } from '@kit/ui/date-field';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { CONTRACT_LENGTHS } from '../../constants/audition.constants';
import { COMPENSATION_UNITS } from '../../lib/casting';
import type { UpdateAuditionInput } from '../../schemas/audition.schema';

// Select value for "no pay unit"; stored as null.
const NO_UNIT = 'none';

// This form stores contract dates as `Date` objects; `DateField` speaks
// `YYYY-MM-DD` strings. Convert at the boundary WITHOUT crossing UTC
// (Critical Rule 19 — no `new Date('YYYY-MM-DD')` / `toISOString()`, which
// shift the day for users west of UTC — the bug the native input had here).
function dateToIso(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoToDate(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

interface ContractTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
}

export function ContractTab({ form }: ContractTabProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans i18nKey="auditions:formTabs.contract.title" />
        </CardTitle>
        <CardDescription>
          <Trans i18nKey="auditions:formTabs.contract.description" />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Contract Type */}
        <FormField
          control={form.control}
          name="contractLength"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey="auditions:formTabs.contract.typeLabel" />
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ''}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        'auditions:formTabs.contract.typePlaceholder',
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CONTRACT_LENGTHS.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-muted-foreground text-xs">
                          {type.description}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Contract Period */}
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="contractStartDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.startDateLabel" />
                </FormLabel>
                <FormControl>
                  <DateField
                    name={field.name}
                    value={dateToIso(field.value)}
                    onChange={(value) => field.onChange(isoToDate(value))}
                    clearable
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:formTabs.contract.startDateHint" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contractEndDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.endDateLabel" />
                </FormLabel>
                <FormControl>
                  <DateField
                    name={field.name}
                    value={dateToIso(field.value)}
                    onChange={(value) => field.onChange(isoToDate(value))}
                    clearable
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:formTabs.contract.endDateHint" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Pay (BAL-1205): shown publicly on the casting page. */}
        <div className="grid gap-4 md:grid-cols-3">
          <FormField
            control={form.control}
            name="compensationAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.compensationAmountLabel" />
                </FormLabel>
                <FormControl>
                  <CurrencyInput
                    aria-label={t(
                      'auditions:formTabs.contract.compensationAmountLabel',
                    )}
                    value={field.value}
                    // CurrencyInput reports an emptied field as 0. Store null
                    // so clearing the amount hides the pay instead of showing 0.
                    onChange={(value) =>
                      field.onChange(value > 0 ? value : null)
                    }
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                  />
                </FormControl>
                <FormDescription>
                  <Trans i18nKey="auditions:formTabs.contract.compensationAmountHint" />
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="compensationCurrency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.compensationCurrencyLabel" />
                </FormLabel>
                <FormControl>
                  <Input
                    maxLength={3}
                    autoCapitalize="characters"
                    data-test="audition-compensation-currency"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="compensationUnit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="auditions:formTabs.contract.compensationUnitLabel" />
                </FormLabel>
                {/* Radix Select cannot hold an empty value, so "no unit" is a
                    sentinel item that stores null. */}
                <Select
                  onValueChange={(value) =>
                    field.onChange(value === NO_UNIT ? null : value)
                  }
                  value={field.value || ''}
                >
                  <FormControl>
                    <SelectTrigger data-test="audition-compensation-unit">
                      <SelectValue
                        placeholder={t(
                          'auditions:formTabs.contract.compensationUnitPlaceholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={NO_UNIT}>
                      <Trans i18nKey="auditions:formTabs.contract.compensationUnitNone" />
                    </SelectItem>
                    {COMPENSATION_UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        <Trans i18nKey={`auditions:compensationUnit.${unit}`} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Roles on offer, one per line. */}
        <FormField
          control={form.control}
          name="roles"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey="auditions:formTabs.contract.rolesLabel" />
              </FormLabel>
              <FormControl>
                <Textarea
                  rows={3}
                  data-test="audition-roles"
                  name={field.name}
                  placeholder={t(
                    'auditions:formTabs.contract.rolesPlaceholder',
                  )}
                  value={(field.value ?? []).join('\n')}
                  onChange={(e) => field.onChange(e.target.value.split('\n'))}
                  onBlur={() => {
                    field.onChange(
                      (field.value ?? []).map((r) => r.trim()).filter(Boolean),
                    );
                    field.onBlur();
                  }}
                />
              </FormControl>
              <FormDescription>
                <Trans i18nKey="auditions:formTabs.contract.rolesHint" />
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
